import { Component, EventEmitter, Input, Output } from "@angular/core";

/**
 * Sentiment scale for {@code FEEDBACK_RATING} attachments (numeric rating per scale in payload).
 *
 * Uses system emoji for faces; short captions (Worst … Excellent) reflect the mapped sentiment tier.
 */
@Component({
  selector: "app-feedback-rating",
  templateUrl: "./feedback-rating.component.html",
  styleUrl: "./feedback-rating.component.scss",
  standalone: false,
})
export class FeedbackRatingComponent {
  @Input() payload: Record<string, any> | null = null;
  @Input() state: Record<string, any> | null = null;
  @Output() ratingSelected = new EventEmitter<{ rating: number }>();

  hoveredLevel: number = 0;

  /** Visible caption under each emoji (tier 1 … 5). */
  static readonly CAPTIONS = [
    "Worst",
    "Poor",
    "Average",
    "Good",
    "Excellent",
  ] as const;

  get levels(): number[] {
    const scale = Number(this.payload?.["scale"]);
    const safe = Number.isFinite(scale) && scale > 0 ? Math.floor(scale) : 5;
    return Array.from({ length: safe }, (_, i) => i + 1);
  }

  get submitted(): boolean {
    return this.state?.["submitted"] === true;
  }

  get submittedRating(): number | null {
    const raw = this.state?.["rating"];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === "string" && raw.trim() !== "") {
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }

  emojiTier(level: number): number {
    const n = this.levels.length;
    if (n <= 1) return 3;
    const idx = level - 1;
    const tierIndex = Math.round((idx / (n - 1)) * 4);
    return tierIndex + 1;
  }

  /** Emoji glyph per sentiment tier (1 = worst … 5 = best). */
  emojiGlyphForTier(tier: number): string {
    switch (tier) {
      case 1:
        return "\u{1F620}"; // 😠 angry — clear negative
      case 2:
        return "\u{1F641}"; // 🙁 slight frown
      case 3:
        return "\u{1F610}"; // 😐 neutral
      case 4:
        return "\u{1F642}"; // 🙂 slight smile
      default:
        return "\u{1F604}"; // 😄 big smile — positive
    }
  }

  captionForLevel(level: number): string {
    const tier = this.emojiTier(level);
    return FeedbackRatingComponent.CAPTIONS[tier - 1] ?? "";
  }

  ariaLabelForLevel(level: number): string {
    const tier = this.emojiTier(level);
    const caption = FeedbackRatingComponent.CAPTIONS[tier - 1];
    return `${caption}, rating ${level} out of ${this.levels.length}`;
  }

  onHover(level: number): void {
    if (this.submitted) return;
    this.hoveredLevel = level;
  }

  onLeave(): void {
    if (this.submitted) return;
    this.hoveredLevel = 0;
  }

  onSelect(level: number): void {
    if (this.submitted) return;
    this.ratingSelected.emit({ rating: level });
  }

  isEmojiActive(level: number): boolean {
    if (this.submitted) {
      return this.submittedRating === level;
    }
    if (this.hoveredLevel > 0) {
      return this.hoveredLevel === level;
    }
    return false;
  }

  isEmojiDimmed(level: number): boolean {
    if (this.submitted) {
      return this.submittedRating !== level;
    }
    if (this.hoveredLevel > 0) {
      return this.hoveredLevel !== level;
    }
    return false;
  }
}
